import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { gardenApi, type GardenRun } from '@/services/api';

/** Garden runs that feed a library: rebuilds now; corpus walks and crawls once they run under it. */
const RUN_LABEL: Record<string, string> = {
  strategy_docket_refresh: 'Library rebuild',
  category_corpus_walk: 'Review walk (corpus)',
  category_ad_corpus_crawl: 'Ad crawl (corpus)',
};

const STATUS_STYLE: Record<string, string> = {
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  running: 'bg-sky-50 text-sky-700 border-sky-200',
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
};

function when(iso: string | null): string {
  return iso ? iso.replace('T', ' ').slice(0, 16) : '—';
}

// The list endpoint names these differently from the detail type.
type ListedRun = GardenRun & { completed_at?: string | null; duration_ms?: number | null };

function duration(run: ListedRun): string {
  let s = run.duration_ms != null ? run.duration_ms / 1000 : null;
  const end = run.finished_at ?? run.completed_at;
  if (s == null && run.started_at && end) s = (Date.parse(end) - Date.parse(run.started_at)) / 1000;
  if (s == null) return '';
  return s < 90 ? `${Math.round(s)}s` : `${Math.round(s / 60)} min`;
}

type Summary = Record<string, unknown>;

function summaryOf(run: GardenRun, stage: string): Summary {
  const outcome = run.stage_outcomes?.[stage] as { summary?: Summary } | undefined;
  return outcome?.summary ?? {};
}

/** What a rebuild read and what it published, in a line or two. */
function RebuildFacts({ run }: { run: GardenRun }) {
  const extract = summaryOf(run, 'docket_extract');
  const publish = summaryOf(run, 'docket_publish');
  const sources = (extract.sources ?? {}) as Record<string, { pack_version?: string }>;
  const skipped = (extract.corpus_skipped ?? {}) as Record<string, string>;
  const missing = (extract.missing ?? []) as string[];
  const version = (publish.version ?? publish.would_publish) as string | undefined;
  const names = Object.keys(sources);
  if (!names.length && !version) return null;
  return (
    <div className="mt-1 space-y-0.5 text-xs text-slate-500">
      {names.length > 0 && (
        <div>
          Read{' '}
          {names.map((n, i) => (
            <span key={n}>
              {i > 0 && ', '}
              <span className={n.startsWith('corpus-') ? 'text-violet-700' : 'text-navy-900'}>{n}</span>
              {sources[n]?.pack_version && <span className="font-mono"> {sources[n].pack_version}</span>}
            </span>
          ))}
        </div>
      )}
      {missing.length > 0 && <div className="text-amber-700">No pack for {missing.join(', ')}</div>}
      {Object.entries(skipped).map(([id, why]) => (
        <div key={id} className="text-amber-700">
          Corpus source {id} held back: {why}
        </div>
      ))}
      {version && (
        <div>
          {publish.dry_run ? 'Would publish' : 'Published'} <span className="font-mono">{version}</span>
        </div>
      )}
    </div>
  );
}

export function DocketRunsTab({ docketId }: { docketId: string }) {
  const clientUid = `cat:${docketId}`;
  const [runs, setRuns] = useState<ListedRun[] | null>(null);
  const [details, setDetails] = useState<Record<string, GardenRun>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    gardenApi
      .listRuns(clientUid, { limit: 50 })
      .then((r) => {
        setRuns(r.runs);
        // The list carries no stage outcomes; fetch them for the latest few rebuilds.
        r.runs
          .filter((run) => run.run_type === 'strategy_docket_refresh')
          .slice(0, 10)
          .forEach((run) =>
            gardenApi
              .getRun(clientUid, run.run_id)
              .then((d) => setDetails((m) => ({ ...m, [run.run_id]: d })))
              .catch(() => undefined),
          );
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [clientUid]);

  useEffect(load, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <p className="text-xs text-slate-500 max-w-3xl">
          Garden runs that build this library. A rebuild merges the clients' mined packs with any corpus source that
          clears the coverage bar; review walks and ad crawls appear here once they run for this library. The mining
          behind today's client packs ran outside the platform, so it is recorded on each pack, not as a run.
        </p>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-navy-900"
        >
          <RefreshCw size={12} className={clsx(loading && 'animate-spin')} /> Refresh
        </button>
      </div>
      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {runs === null ? (
        <div className="flex items-center text-sm text-slate-400">
          <Loader2 size={14} className="animate-spin mr-2" /> Loading runs…
        </div>
      ) : runs.length === 0 ? (
        <div className="text-sm text-slate-500">No runs yet. Rebuild from the header to start one.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {runs.map((run) => (
            <li key={run.run_id} className="py-2.5">
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={clsx(
                    'text-[11px] rounded-full border px-2 py-0.5',
                    STATUS_STYLE[run.status] ?? STATUS_STYLE.pending,
                  )}
                >
                  {run.status}
                </span>
                <span className="font-medium text-navy-900">{RUN_LABEL[run.run_type] ?? run.run_type}</span>
                {details[run.run_id]?.stage_config_overrides?.dry_run === true && (
                  <span className="text-[11px] text-slate-500">dry run</span>
                )}
                <span className="text-xs text-slate-400">
                  {when(run.started_at)}
                  {duration(run) && ` · ${duration(run)}`}
                </span>
                <Link
                  to={`/admin/garden/${encodeURIComponent(clientUid)}?run=${encodeURIComponent(run.run_id)}`}
                  className="ml-auto inline-flex items-center gap-1 text-xs font-mono text-slate-500 hover:text-brand-teal"
                >
                  {run.run_id} <ExternalLink size={11} />
                </Link>
              </div>
              {run.error && <div className="mt-1 text-xs text-red-700">{run.error}</div>}
              {details[run.run_id] && <RebuildFacts run={details[run.run_id]} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
